"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useJobStatusApi = exports.defaultJobStatusApi = exports.JobStatusApi = exports.JobStatusApiError = void 0;
/**
 * Error thrown by the job status API
 */
class JobStatusApiError extends Error {
    status;
    code;
    constructor(message, status, code) {
        super(message);
        this.status = status;
        this.code = code;
        this.name = 'JobStatusApiError';
    }
}
exports.JobStatusApiError = JobStatusApiError;
/**
 * API client for job status operations
 *
 * This service handles communication with the WebKD backend for async job tracking.
 * It provides methods to retrieve job status, handle polling, and manage errors.
 *
 * @example
 * ```typescript
 * const api = new JobStatusApi({
 *   baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:3000',
 *   authToken: 'Bearer your-jwt-token'
 * });
 *
 * try {
 *   const status = await api.getJobStatus('job-123');
 *   console.log('Job status:', status.status);
 * } catch (error) {
 *   console.error('Failed to get job status:', error);
 * }
 * ```
 */
class JobStatusApi {
    config;
    constructor(config) {
        this.config = {
            timeout: 10000,
            authToken: undefined,
            ...config
        };
    }
    /**
     * Get the current status of a job
     *
     * @param jobId - The ID of the job to check
     * @returns Promise resolving to job status response
     * @throws JobStatusApiError when the request fails
     */
    async getJobStatus(jobId) {
        if (!jobId) {
            throw new JobStatusApiError('Job ID is required');
        }
        const url = `${this.config.baseUrl}/job-status/${jobId}`;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);
            // Get auth token from localStorage (same as shared-ui auth system)
            const accessToken = localStorage.getItem('accessToken');
            const authHeader = accessToken ? `Bearer ${accessToken}` : this.config.authToken;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authHeader && { 'Authorization': authHeader })
                },
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            if (!response.ok) {
                const errorData = await this.parseErrorResponse(response);
                throw new JobStatusApiError(errorData.message || `HTTP ${response.status}: ${response.statusText}`, response.status, errorData.code);
            }
            const data = await response.json();
            return this.validateJobStatusResponse(data);
        }
        catch (error) {
            if (error instanceof JobStatusApiError) {
                throw error;
            }
            if (error instanceof TypeError && error.message.includes('fetch')) {
                throw new JobStatusApiError('Network error: Unable to connect to server');
            }
            if (error instanceof Error && error.name === 'AbortError') {
                throw new JobStatusApiError('Request timeout: Server did not respond in time');
            }
            throw new JobStatusApiError(error instanceof Error ? error.message : 'Unknown error occurred');
        }
    }
    /**
     * Poll job status until completion or failure
     *
     * @param jobId - The ID of the job to poll
     * @param onUpdate - Callback fired on each status update
     * @param options - Polling configuration
     * @returns Promise resolving when job completes or fails
     */
    async pollJobStatus(jobId, onUpdate, options = {}) {
        const { interval = 500, maxAttempts = 300, onError } = options;
        let attempts = 0;
        return new Promise((resolve, reject) => {
            const poll = async () => {
                try {
                    attempts++;
                    const status = await this.getJobStatus(jobId);
                    onUpdate(status);
                    // Check if job is complete
                    if (status.status === 'completed' || status.status === 'failed') {
                        resolve(status);
                        return;
                    }
                    // Check if we've exceeded max attempts
                    if (attempts >= maxAttempts) {
                        reject(new JobStatusApiError('Polling timeout: Maximum attempts exceeded'));
                        return;
                    }
                    // Schedule next poll
                    setTimeout(poll, interval);
                }
                catch (error) {
                    const apiError = error instanceof JobStatusApiError
                        ? error
                        : new JobStatusApiError(error instanceof Error ? error.message : 'Polling failed');
                    onError?.(apiError);
                    reject(apiError);
                }
            };
            poll();
        });
    }
    /**
     * Cancel a running job
     *
     * @param jobId - The ID of the job to cancel
     * @returns Promise resolving when cancellation is requested
     */
    async cancelJob(jobId) {
        if (!jobId) {
            throw new JobStatusApiError('Job ID is required');
        }
        const url = `${this.config.baseUrl}/job-status/${jobId}/cancel`;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.config.timeout);
            // Get auth token from localStorage (same as shared-ui auth system)
            const accessToken = localStorage.getItem('accessToken');
            const authHeader = accessToken ? `Bearer ${accessToken}` : this.config.authToken;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(authHeader && { 'Authorization': authHeader })
                },
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            if (!response.ok) {
                const errorData = await this.parseErrorResponse(response);
                throw new JobStatusApiError(errorData.message || `HTTP ${response.status}: ${response.statusText}`, response.status, errorData.code);
            }
        }
        catch (error) {
            if (error instanceof JobStatusApiError) {
                throw error;
            }
            throw new JobStatusApiError(error instanceof Error ? error.message : 'Failed to cancel job');
        }
    }
    /**
     * Update API configuration
     *
     * @param updates - Configuration updates to apply
     */
    updateConfig(updates) {
        this.config = { ...this.config, ...updates };
    }
    /**
     * Parse error response from API
     */
    async parseErrorResponse(response) {
        try {
            const contentType = response.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
                return await response.json();
            }
            return { message: await response.text() };
        }
        catch {
            return { message: response.statusText };
        }
    }
    /**
     * Validate and normalize job status response from API
     */
    validateJobStatusResponse(data) {
        if (!data || typeof data !== 'object') {
            throw new JobStatusApiError('Invalid response format: Expected object');
        }
        if (!data.jobId || typeof data.jobId !== 'string') {
            throw new JobStatusApiError('Invalid response: Missing or invalid jobId');
        }
        if (!data.status || !['waiting', 'active', 'completed', 'failed', 'not-found'].includes(data.status)) {
            throw new JobStatusApiError('Invalid response: Missing or invalid status');
        }
        // Ensure progress object exists with defaults
        const progress = {
            current: 0,
            total: 100,
            percentage: 0,
            ...data.progress
        };
        // Ensure timing object exists with defaults and convert date strings to Date objects
        const timing = {
            createdAt: data.timing?.createdAt ? new Date(data.timing.createdAt) : new Date(),
            startedAt: data.timing?.startedAt ? new Date(data.timing.startedAt) : undefined,
            completedAt: data.timing?.completedAt ? new Date(data.timing.completedAt) : undefined,
            estimatedCompletion: data.timing?.estimatedCompletion ? new Date(data.timing.estimatedCompletion) : undefined
        };
        return {
            jobId: data.jobId,
            status: data.status,
            progress,
            timing,
            result: data.result || undefined,
            metadata: data.metadata || undefined
        };
    }
}
exports.JobStatusApi = JobStatusApi;
/**
 * Default job status API instance
 *
 * This instance uses environment variables for configuration and can be used
 * directly in components. For custom configuration, create a new instance.
 */
exports.defaultJobStatusApi = new JobStatusApi({
    baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:3000',
    timeout: 10000
});
/**
 * Hook to get a configured job status API instance
 *
 * This can be extended to integrate with authentication context
 * to automatically provide auth tokens.
 */
const useJobStatusApi = () => {
    // TODO: Integrate with authentication context when available
    // const { authToken } = useAuth();
    // 
    // return useMemo(() => {
    //   const api = new JobStatusApi({
    //     baseUrl: process.env.REACT_APP_API_URL || 'http://localhost:3000',
    //     authToken,
    //     timeout: 10000
    //   });
    //   return api;
    // }, [authToken]);
    return exports.defaultJobStatusApi;
};
exports.useJobStatusApi = useJobStatusApi;
